// src/contexts/auth/auth-context.tsx
/** @jsxImportSource @emotion/react */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';

import type { AuthContextValue, Profile, RegistrationData, Session, User } from './types';
import { useAuthHooks } from './hooks';
import { AuthDevPanel } from './dev-panel';

import { profileApi } from '../../api/profile/profile';
import { ENVIRONMENT } from '../../env';
import { readStoredTokens } from './helpers';
import { onboardingApi, authApi, clearSession as clearApiSession } from '../../api';
import { useNativeCredentialSync } from '../../hooks/use-native-credential-sync';
import {
  readStoredSession,
  isAccessTokenExpired,
  isIdleTooLong,
  touchLastActive,
} from '../../api/token-storage';
import {
  readAuthHandoffCookie,
  clearAuthHandoffCookie,
  handoffPayloadToSetAuthStateInput,
} from './cookie-session';

import { normalizeAvatarUrl } from '../../utils/avatar-url';
import { identifyUser, resetAnalytics, setGroup, trackEvent, registerPlatformProperties } from '../../utils/analytics';
import { isNative, isWeb } from '../../platform';
import { onAppStateChange } from '../../platform/app-lifecycle';

/* =========================================================================================
   ✅ Avatar Blob Preload + Cache (Context-level, shared by all AppAvatar instances)
========================================================================================= */

type AvatarCacheEntry = {
  key: string; // `${userId}::${version}`
  userId: string;
  version: string | number | null;
  url: string; // object URL created by URL.createObjectURL(blob)
  createdAt: number;
  loading: boolean;
  error: string | null;
  missing: boolean; // ✅ true when backend indicates "no avatar" (e.g. 404)
};

type AvatarState = {
  src?: string | undefined;
  loading: boolean;
  error: string | null;
  missing: boolean;
};

type AvatarBlobContextValue = {
  getAvatarSrc: (args: {
    userId?: string | null;
    version?: string | number | null;
  }) => string | undefined;

  // ✅ NEW: lets consumers avoid infinite re-fetch loops when missing/error
  getAvatarState: (args: {
    userId?: string | null;
    version?: string | number | null;
  }) => AvatarState;

  ensureAvatar: (args: {
    userId?: string | null;
    version?: string | number | null;
    silent?: boolean;
  }) => Promise<void>;

  invalidateAvatar: (args: { userId?: string | null; version?: string | number | null }) => void;
  invalidateAllAvatarsForUser: (args: { userId?: string | null }) => void;
  clearAllAvatars: () => void;
  isAvatarLoading: (args: { userId?: string | null; version?: string | number | null }) => boolean;
};

const AvatarBlobContext = createContext<AvatarBlobContextValue | null>(null);

export const useAvatarBlob = (): AvatarBlobContextValue => {
  const ctx = useContext(AvatarBlobContext);
  if (!ctx) throw new Error('useAvatarBlob must be used within AvatarBlobContext provider');
  return ctx;
};

function computeAvatarVersion(profile: any): string | number | null {
  // ✅ Prefer explicit avatar version bump (frontend or backend)
  if (profile?.avatar_version != null) return profile.avatar_version;

  // fallback (not reliable for avatar changes unless your backend updates it)
  if (profile?.updated_at) return profile.updated_at;

  const raw = typeof profile?.avatar_url === 'string' ? profile.avatar_url : null;
  if (!raw) return null;
  return raw.split('?')[0];
}

function makeAvatarKey(userId: string, version?: string | number | null) {
  return `${userId}::${version ?? '0'}`;
}

// ✅ Robust-ish “no avatar” detection (prevents infinite fetch loops)
function isAvatarNotFoundError(e: any): boolean {
  const status =
    e?.status ??
    e?.statusCode ??
    e?.response?.status ??
    e?.cause?.status ??
    e?.cause?.statusCode ??
    null;

  if (status === 404) return true;

  const msg = String(e?.message ?? e?.error ?? '').toLowerCase();
  if (!msg) return false;

  return (
    msg.includes('404') ||
    msg.includes('not found') ||
    msg.includes('no avatar') ||
    msg.includes('does not exist')
  );
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  /* ------------------------------ Login form ------------------------------ */
  const [email, setEmailState] = useState('');
  const [password, setPasswordState] = useState('');

  const passwordRef = useRef<string>('');
  passwordRef.current = password;

  /* ------------------------------- Core auth ------------------------------ */
  const bootCompleteRef = useRef(false);
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);

  /* -------------------------- Profile state -------------------------- */
  const [profile, setProfile] = useState<Profile | null>(null);
  const lastGoodAvatarUrlRef = useRef<string | undefined>(undefined);

  const [sessionLoading, setSessionLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* -------------------------- Onboarding/Profile -------------------------- */
  const [onboarding, setRegistrationState] = useState<RegistrationData>(() => ({}));
  const [currentStepState, setCurrentStepStateInternal] = useState<number>(() => 0);

  const hooksRef = useRef<any>(null);

  // ✅ PERF: avoid forcing onboarding object updates when step is unchanged
  const setCurrentStepState = useCallback((step: number) => {
    setCurrentStepStateInternal(step);

    const tokens = readStoredTokens();
    if (tokens.access_token && tokens.refresh_token) {
      setRegistrationState((prev) => {
        const prevStep = (prev as any)?.registration_step;
        if (prevStep === step) return prev;

        return {
          ...prev,
          registration_step: step,
        };
      });

      setTimeout(() => {
        hooksRef.current?.persistRegistrationIfNeeded?.();
      }, 0);
    }
  }, []);

  const [userId, setUserId] = useState<string | undefined>(undefined);

  const [profileLoaded, setProfileLoaded] = useState(false);
  const profileSavingRef = useRef(false);
  const setProfileSaving = useCallback((v: boolean) => { profileSavingRef.current = v; }, []);

  // ✅ silent refresh flag (does not affect logic except as data signal)
  const [profileRefreshing, setProfileRefreshing] = useState(false);

  // ✅ keep boot/splash semantics: only depend on profileLoaded
  // profileSaving is for background saves, it shouldn't trigger full-screen loaders
  const loading = !profileLoaded;
  const profileLoading = !profileLoaded;

  /* -------------------------- onboarding status cache -------------------------- */
  const [onboardingHasQuestions, setRegistrationHasQuestions] = useState<boolean | null>(null);
  const [onboardingStatusLoading, setRegistrationStatusLoading] = useState(false);

  /* -------------------------- ensuring profile -------------------------- */
  const [ensuringProfile, setEnsuringProfile] = useState(false);
  const ensuredProfileOnceRef = useRef(false);
  const ensuredOnboardingStatusOnceRef = useRef(false);
  const ensuredPersistRegistrationOnceRef = useRef(false);
  const ensuredOnboardingStatusCalledOnceRef = useRef(false);
  const ensuredProfileCalledOnceRef = useRef(false);

  /* =========================================================================================
     ✅ Avatar Blob Cache Store (lives in AuthProvider)
  ========================================================================================= */

  const avatarCacheRef = useRef<Map<string, AvatarCacheEntry>>(new Map());
  const avatarFetchInFlightRef = useRef<Map<string, Promise<void>>>(new Map());
  const [avatarTick, setAvatarTick] = useState(0);
  const forceAvatarRerender = useCallback(() => setAvatarTick((x) => x + 1), []);

  const invalidateAvatar = useCallback(
    (args: { userId?: string | null; version?: string | number | null }) => {
      const uid = args.userId ?? null;
      if (!uid) return;

      const key = makeAvatarKey(uid, args.version);

      const entry = avatarCacheRef.current.get(key);
      if (!entry) return;

      try {
        if (entry.url) URL.revokeObjectURL(entry.url);
      } catch {}

      avatarCacheRef.current.delete(key);
      avatarFetchInFlightRef.current.delete(key);
      forceAvatarRerender();
    },
    [forceAvatarRerender]
  );

  const invalidateAllAvatarsForUser = useCallback(
    (args: { userId?: string | null }) => {
      const uid = args.userId ?? null;
      if (!uid) return;

      const prefix = `${uid}::`;
      for (const [k, entry] of avatarCacheRef.current.entries()) {
        if (!k.startsWith(prefix)) continue;

        try {
          if (entry.url) URL.revokeObjectURL(entry.url);
        } catch {}

        avatarCacheRef.current.delete(k);
        avatarFetchInFlightRef.current.delete(k);
      }

      forceAvatarRerender();
    },
    [forceAvatarRerender]
  );

  const clearAllAvatars = useCallback(() => {
    try {
      for (const entry of avatarCacheRef.current.values()) {
        try {
          if (entry.url) URL.revokeObjectURL(entry.url);
        } catch {}
      }
    } catch {
      // ignore
    } finally {
      avatarCacheRef.current.clear();
      avatarFetchInFlightRef.current.clear();
      forceAvatarRerender();
    }
  }, [forceAvatarRerender]);

  const getAvatarSrc = useCallback(
    (args: { userId?: string | null; version?: string | number | null }) => {
      const uid = args.userId ?? null;
      if (!uid) return undefined;

      const key = makeAvatarKey(uid, args.version);
      const entry = avatarCacheRef.current.get(key);
      if (!entry) return undefined;

      // ✅ if missing/error, do not report a src
      if (entry.missing) return undefined;
      return entry.url || undefined;
    },
    []
  );

  const getAvatarState = useCallback(
    (args: { userId?: string | null; version?: string | number | null }): AvatarState => {
      const uid = args.userId ?? null;
      if (!uid) return { src: undefined, loading: false, error: null, missing: false };

      const key = makeAvatarKey(uid, args.version);
      const entry = avatarCacheRef.current.get(key);

      if (!entry) return { src: undefined, loading: false, error: null, missing: false };

      return {
        src: entry.missing ? undefined : entry.url || undefined,
        loading: !!entry.loading,
        error: entry.error,
        missing: !!entry.missing,
      };
    },
    []
  );

  const isAvatarLoading = useCallback(
    (args: { userId?: string | null; version?: string | number | null }) => {
      const uid = args.userId ?? null;
      if (!uid) return false;

      const key = makeAvatarKey(uid, args.version);
      const entry = avatarCacheRef.current.get(key);
      return !!entry?.loading;
    },
    []
  );

  const ensureAvatar = useCallback(
    async (args: {
      userId?: string | null;
      version?: string | number | null;
      silent?: boolean;
    }) => {
      const uid = args.userId ?? null;
      if (!uid) return;

      const key = makeAvatarKey(uid, args.version);

      const cached = avatarCacheRef.current.get(key);

      // ✅ Stop infinite retry loops:
      // - if we already have a url, or
      // - we already determined it's missing, or
      // - we already have an error recorded (manual invalidate is the retry mechanism)
      if (cached && !cached.loading) {
        if (cached.url) return;
        if (cached.missing) return;
        if (cached.error) return;
      }

      const existingPromise = avatarFetchInFlightRef.current.get(key);
      if (existingPromise) return existingPromise;

      avatarCacheRef.current.set(key, {
        key,
        userId: uid,
        version: args.version ?? null,
        url: cached?.url ?? '',
        createdAt: Date.now(),
        loading: true,
        error: null,
        missing: false,
      });
      forceAvatarRerender();

      const p = (async () => {
        try {
          const blob = await profileApi.getAvatarBlob(uid);
          const url = URL.createObjectURL(blob);

          const entryNow = avatarCacheRef.current.get(key);
          if (!entryNow) {
            try {
              URL.revokeObjectURL(url);
            } catch {}
            return;
          }

          if (entryNow.url && entryNow.url !== url) {
            try {
              URL.revokeObjectURL(entryNow.url);
            } catch {}
          }

          avatarCacheRef.current.set(key, {
            ...entryNow,
            url,
            loading: false,
            error: null,
            missing: false,
            createdAt: Date.now(),
          });

          forceAvatarRerender();
        } catch (e: any) {
          const entryNow = avatarCacheRef.current.get(key);

          const notFound = isAvatarNotFoundError(e);
          const errMsg = notFound ? 'AVATAR_NOT_FOUND' : (e?.message ?? 'Failed to load avatar');

          if (entryNow) {
            // ✅ mark missing so consumers do NOT keep calling ensureAvatar forever
            avatarCacheRef.current.set(key, {
              ...entryNow,
              loading: false,
              error: errMsg,
              missing: notFound,
            });
          }

          forceAvatarRerender();

          if (!args.silent) {
            if (ENVIRONMENT === 'DEV')
              console.warn('[AvatarBlob] ensureAvatar failed', {
                uid,
                key,
                notFound,
                err: e,
              });
          }
        } finally {
          avatarFetchInFlightRef.current.delete(key);
        }
      })();

      avatarFetchInFlightRef.current.set(key, p);
      return p;
    },
    [forceAvatarRerender]
  );

  /* ------------------------------ Existing hooks wiring ------------------------------ */

  const hooks = useAuthHooks({
    email,
    passwordRef,
    session,
    user,
    profile,
    onboarding,
    currentStepState,
    userId,

    setEmailState,
    setPasswordState,

    setSession,
    setUser,
    setProfile,

    setRegistrationState,
    setCurrentStepState,
    setUserId,

    setProfileLoaded,
    setProfileSaving,

    setProfileRefreshing,

    setSessionLoading,
    setAuthLoading,
    setError,
  });

  hooksRef.current = hooks;

  useEffect(() => {
    const maybe = normalizeAvatarUrl((profile as any)?.avatar_url);
    if (maybe) lastGoodAvatarUrlRef.current = maybe;
  }, [profile]);

  const mergeProfilePatch = useCallback(
    (prev: Profile | null, patch: Partial<Profile>): Profile => {
      const prevAny = (prev ?? {}) as any;
      const patchAny = (patch ?? {}) as any;

      const patchAvatar = patchAny?.avatar_url;
      const patchAvatarNorm = normalizeAvatarUrl(patchAvatar);

      const next: any = { ...prevAny, ...patchAny };

      if ('avatar_url' in patchAny) {
        if (patchAvatarNorm) {
          next.avatar_url = patchAvatarNorm;
          lastGoodAvatarUrlRef.current = patchAvatarNorm;
        } else {
          next.avatar_url = prevAny?.avatar_url ?? lastGoodAvatarUrlRef.current ?? undefined;
        }
      } else {
        if (!normalizeAvatarUrl(next.avatar_url) && lastGoodAvatarUrlRef.current) {
          next.avatar_url = lastGoodAvatarUrlRef.current;
        }
      }

      return next as Profile;
    },
    []
  );

  const normalizeServerProfile = useCallback((updated: Profile | null): Profile | null => {
    if (!updated) return null;
    const u: any = updated as any;

    const maybe = normalizeAvatarUrl(u?.avatar_url);
    if (maybe) {
      lastGoodAvatarUrlRef.current = maybe;
      return { ...u, avatar_url: maybe } as Profile;
    }

    const keep = lastGoodAvatarUrlRef.current;
    if (keep) return { ...u, avatar_url: keep } as Profile;

    return updated;
  }, []);

  const updateProfile = useCallback(
    async (patch: Partial<Profile>): Promise<Profile | null> => {
      try {
        setProfileSaving(true);

        const patchAny = (patch ?? {}) as any;
        const avatarTouched = 'avatar_url' in patchAny;

        if (avatarTouched) {
          const patchAvatarNorm = normalizeAvatarUrl(patchAny?.avatar_url);
          if (patchAvatarNorm) {
            setProfile((prev) => mergeProfilePatch(prev, { avatar_url: patchAvatarNorm } as any));
          }
        }

        const result = await profileApi.updateMe(patch as any);
        trackEvent('profile_updated', { fields_changed: Object.keys(patch) });
        const updatedRaw = (result?.profile ?? result ?? null) as Profile | null;

        // ✅ If avatar was part of the update, bump avatar_version so cache key changes immediately
        const bumped: any =
          updatedRaw && avatarTouched
            ? { ...(updatedRaw as any), avatar_version: Date.now() }
            : updatedRaw;

        const updated = normalizeServerProfile(bumped);

        if (updated) {
          if (avatarTouched) {
            invalidateAllAvatarsForUser({ userId: String((updated as any)?.id ?? '') });
            setProfile(updated);
          } else {
            // Merge without replacing avatar_url to prevent image flicker
            setProfile((prev) => prev ? { ...prev, ...updated, avatar_url: prev.avatar_url } as Profile : updated);
          }
        }
        return updated;
      } finally {
        setProfileSaving(false);
      }
    },
    [mergeProfilePatch, normalizeServerProfile, invalidateAllAvatarsForUser, setProfileSaving]
  );

  const uploadProfilePhoto = useCallback(
    async (file: File): Promise<Profile | null> => {
      try {
        setProfileSaving(true);

        const result = await profileApi.uploadPhoto(file);
        trackEvent('avatar_uploaded');
        const updatedRaw = (result?.profile ?? result ?? null) as Profile | null;

        // ✅ Always bump on photo upload
        const bumped: any = updatedRaw
          ? { ...(updatedRaw as any), avatar_version: Date.now() }
          : null;

        const updated = normalizeServerProfile(bumped);

        if (updated) {
          const uid = String((updated as any)?.id ?? '');

          // Fetch new avatar blob FIRST, then swap the cache entry atomically.
          // This avoids the intermediate "empty cache" state that makes
          // currentAvatarLoading flip to true → appBootLoading → full tree re-render.
          try {
            const blob = await profileApi.getAvatarBlob(uid);
            const url = URL.createObjectURL(blob);

            // Revoke old URLs for this user
            const prefix = `${uid}::`;
            for (const [k, entry] of avatarCacheRef.current.entries()) {
              if (!k.startsWith(prefix)) continue;
              try { if (entry.url) URL.revokeObjectURL(entry.url); } catch {}
              avatarCacheRef.current.delete(k);
              avatarFetchInFlightRef.current.delete(k);
            }

            // Insert new entry with the fetched blob
            const ver = computeAvatarVersion(updated);
            const newKey = makeAvatarKey(uid, ver);
            avatarCacheRef.current.set(newKey, {
              key: newKey,
              userId: uid,
              version: ver,
              url,
              createdAt: Date.now(),
              loading: false,
              error: null,
              missing: false,
            });

            // Single re-render — only AvatarBlobContext consumers update
            forceAvatarRerender();
          } catch {
            // Blob fetch failed — fall back to invalidate + re-fetch
            invalidateAllAvatarsForUser({ userId: uid });
          }

          // ✅ Update profile state so all AppAvatar consumers get the new version key
          setProfile(updated);
        }
        setProfileSaving(false);
        return updated;
      } catch (err) {
        setProfileSaving(false);
        throw err;
      }
    },
    [normalizeServerProfile, invalidateAllAvatarsForUser, forceAvatarRerender, setProfileSaving]
  );

  /* ------------------------------ Boot on mount ------------------------------ */

  useEffect(() => {
    let cleanupCancelled: undefined | (() => void);

    const run = async () => {
      if (ENVIRONMENT === 'DEV') console.log('[Auth][provider] boot() starting');
      cleanupCancelled = await hooksRef.current.boot();
      bootCompleteRef.current = true;
      if (ENVIRONMENT === 'DEV') {
        console.log('[Auth][provider] boot() finished (cleanup fn set?)', {
          hasCleanup: !!cleanupCancelled,
        });
      }
    };

    run();

    const onStorage = (e: StorageEvent) => {
      if (e.key === 'access_token' || e.key === 'refresh_token' || e.key === 'user_id') {
        const t = readStoredTokens();

        if (!t.access_token || !t.refresh_token) {
          setSession(null);
          setUser(null);
          setProfile(null);
          lastGoodAvatarUrlRef.current = undefined;

          setRegistrationHasQuestions(null);
          ensuredProfileOnceRef.current = false;
          ensuredOnboardingStatusOnceRef.current = false;
          ensuredPersistRegistrationOnceRef.current = false;
          ensuredOnboardingStatusCalledOnceRef.current = false;
          ensuredProfileCalledOnceRef.current = false;

          setProfileRefreshing(false);

          clearAllAvatars();
        }
      }
    };

    // Native has a single WebView — no cross-tab storage events
    if (isWeb()) {
      window.addEventListener('storage', onStorage);
    }

    const handleAuthChange = () => {
      void hooksRef.current.syncAuthFromStorage();
      ensuredProfileOnceRef.current = false;
      ensuredOnboardingStatusOnceRef.current = false;
      ensuredPersistRegistrationOnceRef.current = false;
      ensuredOnboardingStatusCalledOnceRef.current = false;
      ensuredProfileCalledOnceRef.current = false;
    };
    window.addEventListener('auth:change', handleAuthChange);

    return () => {
      if (cleanupCancelled) cleanupCancelled();
      if (isWeb()) {
        window.removeEventListener('storage', onStorage);
      }
      window.removeEventListener('auth:change', handleAuthChange);
    };
  }, [clearAllAvatars]);

  /* ─── PostHog: identify user with name/email when profile loads ─── */
  const prevIdentifiedRef = useRef<string | null>(null);
  useEffect(() => {
    const uid = userId ?? (profile as any)?.id ?? null;

    if (uid && profile) {
      // Only re-identify when the user changes
      if (prevIdentifiedRef.current === uid) return;
      prevIdentifiedRef.current = uid;

      const p = profile as any;
      const fullName = [p.first_name, p.last_name].filter(Boolean).join(' ') || undefined;
      registerPlatformProperties();
      identifyUser(
        uid,
        {
          // PostHog special display properties (shown in People list / session replays)
          $name: fullName,
          $email: p.email_address ?? undefined,
          // Custom properties (shown in person profile details)
          first_name: p.first_name ?? undefined,
          last_name: p.last_name ?? undefined,
          name: fullName,
          email: p.email_address ?? undefined,
          company: p.company_name ?? undefined,
          onboarding_completed: p.onboarding_completed ?? false,
          auth_provider: p.auth_provider ?? undefined,
        },
        {
          signed_up_at: p.created_at ?? undefined,
          initial_auth_provider: p.auth_provider ?? undefined,
        },
      );

      const company = p.company_name;
      if (company) {
        setGroup('company', company, { name: company });
      }
    } else if (!uid && prevIdentifiedRef.current) {
      // User signed out — reset PostHog to anonymous
      prevIdentifiedRef.current = null;
      resetAnalytics();
    }
  }, [userId, profile]);

  /* ─── Track real user activity so idle detection is accurate ─── */
  useEffect(() => {
    let lastTouch = 0;
    const THROTTLE_MS = 60_000; // update at most once per minute

    const onActivity = () => {
      const now = Date.now();
      if (now - lastTouch < THROTTLE_MS) return;
      lastTouch = now;
      touchLastActive();
    };

    // Passive listeners — zero perf impact
    window.addEventListener('pointerdown', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity, { passive: true });

    return () => {
      window.removeEventListener('pointerdown', onActivity);
      window.removeEventListener('keydown', onActivity);
    };
  }, []);

  /* ─── Proactive token refresh: adaptive buffer, idle-aware ─── */
  useEffect(() => {
    if (!session?.access_token) return;
    if (!bootCompleteRef.current) return; // Don't schedule refresh until boot finishes

    const stored = readStoredSession();
    if (!stored.expires_at || !stored.refresh_token) return;

    const nowSec = Math.floor(Date.now() / 1000);
    const remainingSec = stored.expires_at - nowSec;

    // Adaptive buffer: 20% of remaining time, clamped [5s, 60s].
    // Prevents a hardcoded 60s buffer from firing instantly on 60s tokens.
    const bufferSec = Math.max(5, Math.min(60, Math.floor(remainingSec * 0.2)));
    const refreshAtSec = stored.expires_at - bufferSec;
    const delayMs = Math.max((refreshAtSec - nowSec) * 1000, 0);

    if (ENVIRONMENT === 'DEV') {
      console.log('[Auth] proactive refresh scheduled in', Math.round(delayMs / 1000), 's (buffer:', bufferSec, 's)');
    }

    let retryTimer: ReturnType<typeof setTimeout>;
    const timer = setTimeout(async () => {
      // If the user has been idle for too long, don't refresh — log them out.
      if (isIdleTooLong()) {
        if (ENVIRONMENT === 'DEV') console.log('[Auth] idle too long — clearing session instead of refreshing');
        clearApiSession({ emit: true });
        return;
      }

      try {
        const result = await authApi.refreshToken();
        if (ENVIRONMENT === 'DEV') console.log('[Auth] proactive refresh succeeded');

        // Update React state so this effect re-triggers for the next cycle
        if (result?.access_token) {
          setSession((prev) =>
            prev
              ? { ...prev, access_token: result.access_token, refresh_token: result.refresh_token }
              : prev,
          );
        }
      } catch {
        if (ENVIRONMENT === 'DEV') console.warn('[Auth] proactive refresh failed');
        const s = readStoredSession();
        if (!s.access_token || !s.refresh_token) {
          // Tokens were cleared (invalid refresh token). Don't dispatch
          // auth:change immediately — the user may be mid-recording or
          // mid-submission. The next API call will naturally 401 and the
          // caller's error handling will save drafts and show a message.
          // The 401 interceptor is the true safety net.
          if (ENVIRONMENT === 'DEV') console.warn('[Auth] tokens cleared after failed refresh — deferring to next API call');
        } else if (!isAccessTokenExpired(s.expires_at)) {
          // Transient failure but token still valid → retry in 5s
          if (ENVIRONMENT === 'DEV') console.log('[Auth] will retry refresh in 5s');
          retryTimer = setTimeout(() => {
            authApi.refreshToken().then((result) => {
              if (result?.access_token) {
                setSession((prev) =>
                  prev
                    ? { ...prev, access_token: result.access_token, refresh_token: result.refresh_token }
                    : prev,
                );
              }
            }).catch(() => {
              // Final retry failed — the 401 interceptor is the last safety net
              if (ENVIRONMENT === 'DEV') console.warn('[Auth] retry refresh also failed');
            });
          }, 5000);
        }
      }
    }, delayMs);

    return () => {
      clearTimeout(timer);
      clearTimeout(retryTimer);
    };
  }, [session?.access_token]);

  /* ─── Track when the tab goes hidden ─── */
  const tabHiddenSinceRef = useRef<number | null>(null);

  /* ─── Visibility change: idle-aware refresh when tab returns to focus ─── */
  useEffect(() => {
    const onVisibilityChange = async () => {
      if (document.hidden) {
        tabHiddenSinceRef.current = Date.now();
        return;
      }

      // Skip refresh during boot — boot already handles token refresh.
      // On desktop, hard-refresh can fire visibilitychange (hidden→visible)
      // during page init, racing with boot's refresh and consuming the
      // single-use Supabase refresh token twice.
      if (!bootCompleteRef.current) return;

      // Tab becoming visible again
      const stored = readStoredSession();
      if (!stored.refresh_token) return;

      tabHiddenSinceRef.current = null;


      // If the user has been idle for too long, log them out instead of refreshing.
      if (isIdleTooLong()) {
        if (ENVIRONMENT === 'DEV') console.log('[Auth] idle too long on visibility change — clearing session');
        clearApiSession({ emit: true });
        return;
      }


      // Always validate session when the app returns to foreground.
      // If refresh tokens were revoked (e.g. global signout while app
      // was backgrounded), this will fail and clear the session.
      try {
        const result = await authApi.refreshToken();
        if (result?.access_token) {
          setSession((prev) =>
            prev
              ? { ...prev, access_token: result.access_token, refresh_token: result.refresh_token }
              : prev,
          );
        }
      } catch {
        if (ENVIRONMENT === 'DEV') {
          const clearedSession = readStoredSession();
          if (!clearedSession.access_token || !clearedSession.refresh_token) {
            console.warn('[Auth] visibility refresh failed, tokens cleared — session revoked');
          }
        }
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  /* ─── Native: foreground detection via Capacitor App plugin ─── */
  useEffect(() => {
    if (!isNative()) return;
    return onAppStateChange(async (isActive) => {
      if (!isActive) return;
      // Same logic as the visibilitychange handler: check idle + refresh
      if (isIdleTooLong()) {
        clearApiSession({ emit: true });
        return;
      }
      try {
        const result = await authApi.refreshToken();
        if (result?.access_token) {
          setSession((prev) =>
            prev
              ? { ...prev, access_token: result.access_token, refresh_token: result.refresh_token }
              : prev,
          );
        }
      } catch {
        // silent — token may still be valid
      }
    });
  }, []);

  useEffect(() => {
    if (!(onboarding as any)?.password) return;
    if (ensuredPersistRegistrationOnceRef.current) return;
    ensuredPersistRegistrationOnceRef.current = true;
    hooksRef.current.persistRegistrationIfNeeded();
  }, [onboarding, userId]);

  const isAuthedNow = useMemo(() => {
    const tokens = readStoredTokens();
    const authedByMemory = !!user || !!session?.access_token;
    const authedByStorage = !!tokens.access_token && !!tokens.refresh_token;
    return authedByMemory || authedByStorage;
  }, [user, session]);

  const ensureProfile = useCallback(async () => {
    if (!isAuthedNow) return;
    if (profile) return;
    if (ensuringProfile) return;
    if (ensuredProfileOnceRef.current) return;

    ensuredProfileOnceRef.current = true;
    setEnsuringProfile(true);
    try {
      const me = await profileApi.getMe();
      const normalized = normalizeServerProfile((me as any) ?? null);
      if (normalized) setProfile(normalized);
    } catch {
      // ignore
    } finally {
      setEnsuringProfile(false);
    }
  }, [isAuthedNow, profile, ensuringProfile, normalizeServerProfile]);

  const ensureOnboardingStatus = useCallback(async () => {
    if (!isAuthedNow) return;
    if (onboardingHasQuestions !== null) return;
    if (onboardingStatusLoading) return;
    if (ensuredOnboardingStatusOnceRef.current) return;

    ensuredOnboardingStatusOnceRef.current = true;
    setRegistrationStatusLoading(true);
    try {
      const res: any = await onboardingApi.getStatus();
      const total =
        typeof res?.total_questions === 'number'
          ? res.total_questions
          : Array.isArray(res?.questions)
            ? res.questions.length
            : 0;

      setRegistrationHasQuestions(total > 0);
    } catch {
      setRegistrationHasQuestions(true);
    } finally {
      setRegistrationStatusLoading(false);
    }
  }, [isAuthedNow, onboardingHasQuestions, onboardingStatusLoading]);

  useEffect(() => {
    if (sessionLoading) return;
    if (profileLoading) return;

    if (!isAuthedNow) {
      setRegistrationHasQuestions(null);
      return;
    }

    if (ensuredProfileCalledOnceRef.current) return;
    ensuredProfileCalledOnceRef.current = true;
    void ensureProfile();
  }, [sessionLoading, profileLoading, isAuthedNow, ensureProfile]);

  useEffect(() => {
    if (sessionLoading) return;
    if (profileLoading) return;
    if (!isAuthedNow) return;
    if (!profile) return;
    if (ensuredOnboardingStatusCalledOnceRef.current) return;

    ensuredOnboardingStatusCalledOnceRef.current = true;
    void ensureOnboardingStatus();
  }, [sessionLoading, profileLoading, isAuthedNow, profile, ensureOnboardingStatus]);

  const rehydrateAuthFromCookieHandoff = useCallback(async (): Promise<boolean> => {
    if (isNative()) return false; // No cookie handoff on native
    const payload = readAuthHandoffCookie();
    if (!payload) return false;

    const ageMs = Date.now() - (payload.created_at_ms ?? 0);
    if (ageMs > 30 * 60 * 1000) {
      clearAuthHandoffCookie();
      return false;
    }

    await hooksRef.current.setAuthStateFromTokens(handoffPayloadToSetAuthStateInput(payload));
    await hooksRef.current.syncSession();

    clearAuthHandoffCookie();
    return true;
  }, []);

  const profileReadyForRouting = !sessionLoading && !profileLoading && (!isAuthedNow || !!profile);

  /* =========================================================================================
     ✅ PRELOAD CURRENT USER AVATAR DURING BOOT (and re-preload on avatar changes)
  ========================================================================================= */

  const currentUserAvatarUserId = String((profile as any)?.id ?? '');
  const currentUserAvatarVersion = useMemo(() => computeAvatarVersion(profile), [profile]);

  const lastPreloadedKeyRef = useRef<string>('');
  useEffect(() => {
    if (!currentUserAvatarUserId) return;
    if (!profile?.avatar_url) return; // no avatar uploaded — skip fetch

    const key = makeAvatarKey(currentUserAvatarUserId, currentUserAvatarVersion);
    if (lastPreloadedKeyRef.current === key) return;

    lastPreloadedKeyRef.current = key;

    void ensureAvatar({
      userId: currentUserAvatarUserId,
      version: currentUserAvatarVersion,
      silent: true,
    });
  }, [currentUserAvatarUserId, currentUserAvatarVersion, ensureAvatar, profile?.avatar_url]);

  // ✅ This blocks splash until avatar is actually ready (or failed),
  // including the very first render where the preload effect hasn't run yet.
  // After initial boot completes, avatar reloads never block the UI again.
  const avatarBootDoneRef = useRef(false);

  // Safety timeout: never let avatar loading block boot for more than 5 seconds
  const [avatarTimedOut, setAvatarTimedOut] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (!avatarBootDoneRef.current) {
        avatarBootDoneRef.current = true;
        setAvatarTimedOut(true);
      }
    }, 5000);
    return () => window.clearTimeout(t);
  }, []);

  // @ts-expect-error kept for future reuse — avatar boot-blocking logic
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const currentAvatarLoading = useMemo(() => {
    // Once boot has completed, never block again (prevents whole-screen
    // refresh when the user changes their avatar photo).
    if (avatarBootDoneRef.current) return false;
    if (avatarTimedOut) return false;

    if (!isAuthedNow) return false;
    if (!currentUserAvatarUserId) return false;

    const key = makeAvatarKey(currentUserAvatarUserId, currentUserAvatarVersion);
    const entry = avatarCacheRef.current.get(key);

    if (!entry) return true;
    if (entry.loading) return true;

    // ✅ if missing/error, do NOT block boot
    avatarBootDoneRef.current = true;
    return false;
  }, [isAuthedNow, currentUserAvatarUserId, currentUserAvatarVersion, avatarTick, avatarTimedOut]);

  // Avatar preload continues in the background (useEffect lines 936-949)
  // but no longer blocks the boot splash — it's cosmetic, not needed for routing.
  const appBootLoading =
    sessionLoading ||
    profileLoading ||
    ensuringProfile;

  const {
    setEmail,
    setPassword,
    clearPassword,
    resetLoginForm,

    setRegistration: setRegistrationFn,
    setCurrentStep: setCurrentStepFn,

    signUp,
    signIn,
    signinWithPassword,
    signInWithGoogle,
    signUpWithGoogle,
    biometricLogin,
    signOut,
    syncSession,
    syncAuthFromStorage,

    devTokens,
    clearState,
    hardLogout,
    dump,

    isDevEnv,
    devToolsEnabled,
  } = hooks;

  const value = useMemo<AuthContextValue>(
    () => ({
      email,
      password,
      setEmail,
      setPassword,
      clearPassword,
      resetLoginForm,

      user,
      session,
      sessionLoading,
      authLoading,

      profile,
      updateProfile,
      uploadProfilePhoto,

      onboarding,
      setRegistration: setRegistrationFn,
      currentStep: currentStepState,
      setCurrentStep: setCurrentStepFn,

      userId,
      profileLoading,

      profileRefreshing,

      loading,

      rehydrateAuthFromCookieHandoff,

      onboardingHasQuestions,
      onboardingStatusLoading,
      ensureOnboardingStatus,

      appBootLoading,
      profileReadyForRouting,

      error,

      signUp,
      signIn,
      signinWithPassword,
      signInWithGoogle,
      signUpWithGoogle,
      biometricLogin,

      signOut,
      syncSession,
      syncAuthFromStorage,

      dev: {
        isDevEnv,
        devToolsEnabled,
        tokens: devTokens,
        clearState,
        hardLogout,
        dump: () => ({
          ...dump(),
          tokenStorage: readStoredTokens(),
        }),
      },
    }),
    [
      email,
      password,
      setEmail,
      setPassword,
      clearPassword,
      resetLoginForm,

      user,
      session,
      sessionLoading,
      authLoading,

      profile,
      updateProfile,
      uploadProfilePhoto,

      onboarding,
      setRegistrationFn,
      currentStepState,
      setCurrentStepFn,

      userId,
      profileLoading,

      profileRefreshing,

      loading,

      rehydrateAuthFromCookieHandoff,

      onboardingHasQuestions,
      onboardingStatusLoading,
      ensureOnboardingStatus,

      appBootLoading,
      profileReadyForRouting,

      error,

      signUp,
      signIn,
      signinWithPassword,
      signInWithGoogle,
      signUpWithGoogle,
      biometricLogin,
      signOut,
      syncSession,
      syncAuthFromStorage,

      isDevEnv,
      devToolsEnabled,
      devTokens,
      clearState,
      hardLogout,
      dump,
    ]
  );

  const avatarCtxValue = useMemo<AvatarBlobContextValue>(
    () => ({
      getAvatarSrc,
      getAvatarState,
      ensureAvatar,
      invalidateAvatar,
      invalidateAllAvatarsForUser,
      clearAllAvatars,
      isAvatarLoading,
    }),
    [
      getAvatarSrc,
      getAvatarState,
      ensureAvatar,
      invalidateAvatar,
      invalidateAllAvatarsForUser,
      clearAllAvatars,
      isAvatarLoading,
    ]
  );

  // Keep native Keystore/Keychain credentials in sync with session token rotation
  useNativeCredentialSync(userId ?? null, session?.refresh_token ?? null);

  useEffect(() => {
    return () => {
      clearAllAvatars();
    };
  }, [clearAllAvatars]);

  return (
    <AuthContext.Provider value={value}>
      <AvatarBlobContext.Provider value={avatarCtxValue}>
        {ENVIRONMENT === 'DEV' ? <AuthDevPanel /> : null}
        {children}
      </AvatarBlobContext.Provider>
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
